import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { LogOut, LayoutDashboard, Store, Sparkles, Menu, ShieldCheck } from "lucide-react";
import { isAdminHost, urlOnAdmin, urlOnPublic } from "../../lib/hosts";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const adminMode = isAdminHost();

  const linkCls = (path) =>
    `text-sm font-medium transition-colors ${
      loc.pathname === path ? "text-black" : "text-neutral-500 hover:text-black"
    }`;

  const mobileLinks = [
    { to: "/", label: "Home" },
    { to: "/marketplace", label: "All Brands" },
    { to: "/marketplace?type=ecom&category=fashion", label: "Fashion" },
    { to: "/marketplace?type=ecom&category=beauty", label: "Beauty" },
    { to: "/my/referrals", label: "My Referrals" },
    { to: "/about", label: "About" },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between gap-2">
        <Link to={adminMode ? "/login" : "/"} data-testid="brand-logo" className="flex items-center gap-2 min-w-0 flex-shrink-0">
          <div className={`w-7 h-7 rounded-md grid place-items-center flex-shrink-0 ${adminMode ? "bg-[#0055FF]" : "bg-black"}`}>
            {adminMode ? <ShieldCheck className="w-4 h-4 text-white" /> : <Sparkles className="w-4 h-4 text-white" />}
          </div>
          <span className="font-display text-base sm:text-lg font-semibold tracking-tight whitespace-nowrap">
            {adminMode ? "OE Switch" : "Offer Express"}
          </span>
          <span className="badge-mono text-neutral-500 ml-2 hidden lg:inline whitespace-nowrap">
            / {adminMode ? "switch.offer.express" : "offer.express"}
          </span>
        </Link>

        {!adminMode && (
          <nav className="hidden md:flex items-center gap-6 lg:gap-8 min-w-0">
            <Link to="/" data-testid="nav-home" className={linkCls("/")}>Home</Link>
            <Link to="/marketplace?type=ecom" data-testid="nav-marketplace" className={linkCls("/marketplace")}>All Brands</Link>
            <Link to="/marketplace?type=ecom&category=fashion" data-testid="nav-fashion" className="text-sm font-medium text-neutral-500 hover:text-black whitespace-nowrap">Fashion</Link>
            <Link to="/marketplace?type=ecom&category=beauty" data-testid="nav-beauty" className="text-sm font-medium text-neutral-500 hover:text-black">Beauty</Link>
            <Link to="/my/referrals" data-testid="nav-my-referrals" className="text-sm font-medium text-neutral-500 hover:text-black whitespace-nowrap">My Referrals</Link>
            <a
              href={typeof window !== "undefined" && /localhost|preview\.emergentagent/.test(window.location.hostname)
                ? "/?loans=1"
                : "https://loans.offer.express/"}
              data-testid="nav-loan-club"
              className="text-sm font-semibold text-[#0055FF] hover:opacity-80 whitespace-nowrap"
            >
              Loan Express
            </a>
            <Link to="/about" data-testid="nav-about" className={linkCls("/about")}>About</Link>
          </nav>
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          {user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="nav-dashboard-btn"
                onClick={() => nav(user.role === "admin" ? "/admin" : "/partner")}
              >
                <LayoutDashboard className="w-4 h-4 mr-1.5" />
                {user.role === "admin" ? "Admin" : "Partner"}
              </Button>
              <Button variant="outline" size="sm" data-testid="logout-btn" onClick={async () => { await logout(); nav(adminMode ? "/login" : "/"); }}>
                <LogOut className="w-4 h-4 mr-1.5" />
                Sign out
              </Button>
            </>
          ) : (
            !adminMode ? (
              <>
                <Link to="/marketplace" className="hidden sm:block">
                  <Button variant="ghost" size="sm" data-testid="nav-browse-btn"><Store className="w-4 h-4 mr-1.5" />Browse</Button>
                </Link>
                {/* Partner login lives on the public site now.
                    Admin login is behind switch.offer.express and is
                    intentionally not surfaced from the public nav. */}
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="nav-partner-link" className="text-neutral-500 hover:text-black">
                    Partner login
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/login">
                <Button size="sm" data-testid="nav-login-btn" className="bg-[#0055FF] hover:bg-[#0044cc] text-white">Admin sign in</Button>
              </Link>
            )
          )}

          {/* Mobile hamburger — only on public host */}
          {!adminMode && (
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden ml-1"
                data-testid="nav-mobile-toggle"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] max-w-sm" data-testid="mobile-drawer">
              <SheetHeader className="text-left border-b border-black/5 pb-4">
                <SheetTitle className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-black grid place-items-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-display text-base font-semibold tracking-tight">Offer Express</span>
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1">
                {mobileLinks.map((l) => (
                  <Link
                    key={l.to + l.label}
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    onClick={() => setMobileOpen(false)}
                    className="px-3 py-3 rounded-md font-display text-lg font-medium text-neutral-800 hover:bg-neutral-100"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
              <div className="border-t border-black/5 mt-6 pt-6 space-y-2">
                {user ? (
                  <>
                    <Button
                      className="w-full justify-start bg-black hover:bg-[#0055FF] text-white"
                      data-testid="mobile-dashboard-btn"
                      onClick={() => {
                        setMobileOpen(false);
                        nav(user.role === "admin" ? "/admin" : "/partner");
                      }}
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {user.role === "admin" ? "Admin console" : "Partner console"}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      data-testid="mobile-logout-btn"
                      onClick={async () => {
                        setMobileOpen(false);
                        await logout();
                        nav("/");
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </Button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setMobileOpen(false)}>
                    <Button data-testid="mobile-signin-btn" className="w-full bg-black hover:bg-[#0055FF] text-white">
                      Sign in
                    </Button>
                  </Link>
                )}
              </div>
              <div className="mt-8 badge-mono text-neutral-400">/ offer.express</div>
            </SheetContent>
          </Sheet>
          )}
        </div>
      </div>
    </header>
  );
}
